import { useEffect, useState } from 'react';

/** Classic North-American dual ring tone. */
const RING_FREQUENCIES_HZ = [440, 480] as const;
/** 2s on / 4s off cadence. */
const RING_ON_SEC = 2;
const RING_PERIOD_MS = 6_000;
/** Audible on laptop speakers but still polite — it repeats until the queue empties. */
const RING_GAIN = 0.18;

/**
 * One AudioContext for the app's lifetime (module singleton, mirroring
 * `voice-device.ts`). Browser autoplay policy only lets a context run after
 * a user gesture — a context created fresh when the ring starts (a WebSocket
 * event) stays `suspended` on any tab the user hasn't touched since load,
 * which is exactly the "popup shows but no sound" bug. The shared context is
 * unlocked once by the first gesture anywhere and reused by every ring after.
 */
let sharedContext: AudioContext | null = null;
let removeUnlockListeners: (() => void) | null = null;

function webAudioSupported(): boolean {
    return typeof window !== 'undefined' && 'AudioContext' in window;
}

function getSharedContext(): AudioContext | null {
    if (!webAudioSupported()) return null;
    sharedContext ??= new AudioContext();
    return sharedContext;
}

/**
 * Capture-phase pointerdown/keydown listeners that resume the shared context
 * and remove themselves once it is running. Installing is idempotent, so a
 * ring that starts pre-gesture re-arms it and gets sound mid-cadence on the
 * user's next click or keypress.
 */
function installUnlockListeners(): void {
    if (removeUnlockListeners || !webAudioSupported()) return;
    const unlock = (): void => {
        const context = getSharedContext();
        if (!context || context.state === 'running') {
            removeUnlockListeners?.();
            return;
        }
        void context
            .resume()
            .then(() => {
                if (context.state === 'running') removeUnlockListeners?.();
            })
            .catch(() => undefined);
    };
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
    removeUnlockListeners = () => {
        window.removeEventListener('pointerdown', unlock, true);
        window.removeEventListener('keydown', unlock, true);
        removeUnlockListeners = null;
    };
}

/** Test-only: drop the shared context and detach the unlock listeners. */
export function resetRingtoneAudioForTests(): void {
    removeUnlockListeners?.();
    sharedContext = null;
}

/**
 * Ringtone for the widget's ringing queue. The Twilio SDK's own incoming
 * sound is disabled in `voice-device.ts` (it only understands a single
 * call and goes silent with `allowIncomingWhileBusy`), so the widget rings
 * here instead: iff `enabled` — queue nonempty && no active call — and it
 * stops the moment that flips false or the widget unmounts.
 *
 * Synthesized via Web Audio on the module's gesture-unlocked shared context.
 * Returns `true` while ringing is active but autoplay policy is still
 * blocking the sound — the widget shows a "click anywhere" hint off it, and
 * the next gesture starts the ring mid-cadence (scheduled gain pulses are
 * relative to `currentTime`, which freezes while suspended).
 */
export function useRingtone(enabled: boolean): boolean {
    const [audioBlocked, setAudioBlocked] = useState(false);

    // Arm the unlock at mount: the first gesture anywhere in the app primes
    // the shared context long before the first call arrives.
    useEffect(() => {
        installUnlockListeners();
    }, []);

    useEffect(() => {
        if (!enabled) return;
        const context = getSharedContext();
        if (!context) return;

        installUnlockListeners(); // re-arm: this ring may have started pre-gesture

        const syncBlocked = (): void => setAudioBlocked(context.state !== 'running');
        syncBlocked();
        context.addEventListener('statechange', syncBlocked);
        if (context.state === 'suspended') void context.resume().catch(() => undefined);

        const gain = context.createGain();
        gain.gain.value = 0;
        gain.connect(context.destination);

        const oscillators = RING_FREQUENCIES_HZ.map((frequency) => {
            const oscillator = context.createOscillator();
            oscillator.frequency.value = frequency;
            oscillator.connect(gain);
            oscillator.start();
            return oscillator;
        });

        const pulse = (): void => {
            const now = context.currentTime;
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(RING_GAIN, now);
            gain.gain.setValueAtTime(0, now + RING_ON_SEC);
        };
        pulse();
        const interval = setInterval(pulse, RING_PERIOD_MS);

        return () => {
            clearInterval(interval);
            for (const oscillator of oscillators) {
                oscillator.stop();
                oscillator.disconnect();
            }
            gain.disconnect();
            context.removeEventListener('statechange', syncBlocked);
            setAudioBlocked(false); // the shared context stays open for the next ring
        };
    }, [enabled]);

    return audioBlocked;
}
